/**
 * Composants de l'identité MAP v1.0 (IDENTITE.md §7, ADR 0018) : pastille d'indicateur, plaque-chiffre, barre de mandat.
 * Aucun chiffre ici : l'appelant fournit des valeurs déjà formatées (format.ts) issues du moteur ou du contenu.
 *
 * Neutralité (CLAUDE.md règle 5) : la couleur d'une pastille dit l'effet d'un INDICATEUR sur le pays, dans son sens propre
 * (dette qui baisse = améliore). Jamais une mesure, un levier, un montant de recettes / dépenses / dette, ni un camp : ceux-là
 * restent à l'encre.
 */
import { escapeHtml as e } from "../format.js";
import { icon } from "../icons.js";

export type Effect = "better" | "worse" | "equal";

const EFFECT_WORD: Record<Effect, string> = { better: "améliore", worse: "dégrade", equal: "neutre" };
const EFFECT_CLASS: Record<Effect, string> = { better: "fx-better", worse: "fx-worse", equal: "fx-equal" };

/**
 * Pastille d'indicateur : flèche (sens de la variation : `delta` > 0 monte, < 0 descend, 0 égal), valeur signée, mot masqué
 * pour les lecteurs d'écran. `effect` = effet sur le pays dans le sens propre de l'indicateur.
 * @param value valeur signée déjà formatée (« +0,5 pt », « −3,1 »).
 * @param against ce à quoi la variation est comparée, lu par les lecteurs d'écran (« face à si rien ne change »).
 */
export function pastille(effect: Effect, delta: number, value: string, against = ""): string {
  const dir = effect === "equal" || delta === 0 ? "egal" : delta > 0 ? "fleche-haut" : "fleche-bas";
  return `<span class="pastille ${EFFECT_CLASS[effect]}">${icon(dir, 16)}<span class="pv">${e(value)}</span><span class="sr"> : ${EFFECT_WORD[effect]}${against ? ` ${e(against)}` : ""}</span></span>`;
}

/** Plaque-chiffre (héros) : fond jaune, libellé en légende grasse, montant en Archivo condensé. Une par écran au plus. */
export function plate(label: string, amountHtml: string, opts: { id?: string; note?: string } = {}): string {
  return `<div class="plate"${opts.id ? ` id="${e(opts.id)}"` : ""}><p class="plate-l">${label}</p><p class="plate-v num">${amountHtml}</p>${opts.note ? `<p class="plate-n">${opts.note}</p>` : ""}</div>`;
}

/**
 * Barre de mandat : un segment par année de budget du mandat (nombre = `mandate_years` du contenu), segments remplis
 * jusqu'à l'année en cours. `current` = index 1… de l'année en cours (0 = mandat pas commencé).
 */
export function mandateBar(years: number[], current: number, opts: { onBrand?: boolean } = {}): string {
  const n = years.length;
  const cur = Math.max(0, Math.min(n, current));
  const label = cur > 0 ? `Année ${cur} sur ${n} du mandat (${years[cur - 1]})` : `Mandat de ${n} ans, pas encore commencé`;
  const segs = years.map((y, i) => `<li class="${i < cur ? "on" : ""}${i === cur - 1 ? " now" : ""}"><span class="sr">${y}</span></li>`).join("");
  return `<div class="mandate${opts.onBrand ? " on-brand" : ""}"><ol class="mbar" role="img" aria-label="${e(label)}">${segs}</ol><p class="mlab" aria-hidden="true">${cur > 0 ? `<b>${years[cur - 1]}</b> · année ${cur} sur ${n}` : `${years[0] ?? ""}–${years[n - 1] ?? ""}`}</p></div>`;
}
